import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const useFileUpload = (initialFiles = []) => {
  const [uploadedFiles, setUploadedFiles] = useState(initialFiles);
  
  const maxFileSize = 10 * 1024 * 1024;

  const uploadFiles = useCallback(
    async (file, uniqueId) => {
      const formData = new FormData();
      formData.append("file", file);

      try {
        if (file.type.startsWith("image/")) {
          const res = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/upload/image`,
            {
              method: "POST",
              body: formData,
              credentials: 'include',
            }
          );
          
          if (res.status === 401) {
            if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
              window.location.href = '/login?expired=true';
            }
            return;
          }
          
          if (!res.ok) {
            if (res.status === 422) {
              throw new Error(`Cannot upload ${file.name}`);
            }
            throw new Error(`An error occurred while processing ${file.name}`);
          }
          
          const data = await res.json();
          if (data.error) {
            throw new Error(data.error);
          }
          return {
            id: uniqueId,
            type: data.type,
            name: data.name,
            content: data.content,
          };
        } 
        
        else {
          const res = await fetch(
            `${process.env.REACT_APP_FASTAPI_URL}/upload/file`,
            {
              method: "POST",
              body: formData,
              credentials: 'include',
            }
          );
          
          if (res.status === 401) {
            if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
              window.location.href = '/login?expired=true';
            }
            return;
          }
          
          if (!res.ok) {
            if (res.status === 422) {
              throw new Error(`Cannot upload ${file.name}`);
            }
            if (res.status === 413) {
              throw new Error(`${file.name} exceeds the file size limit`);
            }
            throw new Error(`An error occurred while processing ${file.name}`);
          }
          
          const data = await res.json();
          if (data.error) {
            throw new Error(data.error);
          }
          return {
            id: uniqueId,
            type: data.type,
            name: data.name,
            content: data.content,
            file_path: data.file_path
          };
        }
      } catch (error) {
        throw error;
      }
    },
    []
  );

  const processFiles = useCallback(
    async (files, onError, imageSupport, maxAllowed = 10) => {
      let acceptedFiles = [];
      const currentCount = uploadedFiles.length;
      const remaining = maxAllowed - currentCount;
      
      const imageFiles = files.filter((file) => file.type.startsWith("image/"));
      if (imageFiles.length > 0 && !imageSupport) {
        onError?.("This model does not support image uploads.");
        return;
      }
      
      if (files.length > remaining) {
        onError?.(`You can upload up to ${maxAllowed} files.`);
        acceptedFiles = files.slice(0, remaining);
      } else {
        acceptedFiles = files;
      }
      
      const filePairs = acceptedFiles.map((file) => {
        const uniqueId = uuidv4();
        const preview = file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined;
        return { file, uniqueId, preview };
      });

      setUploadedFiles((prev) => [
        ...prev,
        ...filePairs.map(({ file, uniqueId, preview }) => ({
          id: uniqueId,
          name: file.name,
          type: file.type,
          preview,
        })),
      ]);

      await Promise.all(
        filePairs.map(async ({ file, uniqueId }) => {
          try {
            const result = await uploadFiles(file, uniqueId);
            setUploadedFiles((prev) =>
              prev.map((item) =>
                item.id === uniqueId ? { ...item, ...result } : item
              )
            );
          } catch (err) {
            onError?.(err.message);
            setUploadedFiles((prev) => {
              const target = prev.find((item) => item.id === uniqueId);
              if (target && target.preview) {
                URL.revokeObjectURL(target.preview);
              }
              return prev.filter((item) => item.id !== uniqueId);
            });
          }
        })
      );
      
      return uploadedFiles;
    },
    [uploadedFiles, uploadFiles, setUploadedFiles]
  );
  
  const removeFile = useCallback((fileId) => {
    setUploadedFiles((prev) => {
      const target = prev.find((file) => file.id === fileId);
      if (target && target.preview) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((file) => file.id !== fileId);
    });
  }, [setUploadedFiles]);

  return {
    uploadedFiles,
    setUploadedFiles,
    uploadFiles,
    processFiles,
    removeFile,
    maxFileSize
  };
}; 